import { contractTypeOptions, noticePeriodOptions } from "@nano-portal/customers"
import { isNil, round } from "@nano-portal/shared"
import { Controller } from "react-hook-form"
import { Spacer } from "../../../../../components/spacer/Spacer"
import { theme } from "../../../../../styles/stitches.config"
import { getFormError, getFormSuccess } from "../../../../../utils/form"
import { terminationOptions, useTerminationFormOptions } from "../acquisitionForm.utils"
import { DateInputBlock } from "../components/DateInputBlock"
import { FileInput, FileInputDisclaimer } from "../components/FileInput"
import { FormItem, FormLabel } from "../components/Form"
import { FormInput, InputDisclaimer } from "../components/Input"
import { FormSelect } from "../components/Select"
import { useConsumptionPointFormContext } from "./consumptionPointForm.utils"

export function ConsumptionPointContractInfoSection() {
  const form = useConsumptionPointFormContext()
  const terminationFormOptions = useTerminationFormOptions()

  return (
    <>
      <Controller
        control={form.control}
        name="currentSupplier"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Stávající dodavatel</FormLabel>
            <FormInput
              id="supplier"
              type="text"
              placeholder="např. ČEZ Prodej, s.r.o"
              {...field}
              error={getFormError({ form, name: "currentSupplier" })}
              success={getFormSuccess({ form, name: "currentSupplier" })}
            />
          </FormItem>
        )}
      />

      <Spacer size={theme.space.s9} />

      <Controller
        control={form.control}
        name="contractInfo.terminationOption"
        render={({ field: { onChange, ...field } }) => (
          <FormItem>
            <FormLabel>Smlouva u stávajícího dodavatele</FormLabel>
            <FormSelect
              id="terminationOption"
              options={terminationFormOptions}
              onChange={(e) => {
                const value = e.target.value
                const shouldTerminate = value === terminationOptions.TERMINATE
                onChange(e)

                if (shouldTerminate && isNil(form.getValues("contractInfo.currentContract"))) {
                  form.setValue("contractInfo.currentContract", {
                    contractType: "VARIABLE_TERM",
                    noticePeriod: "ONE_MONTH",
                  })
                }
              }}
              {...field}
              error={getFormError({ form, name: "contractInfo.terminationOption" })}
              success={getFormSuccess({ form, name: "contractInfo.terminationOption" })}
            />
          </FormItem>
        )}
      />

      <Spacer size={theme.space.s9} />

      <TerminationOptionSection />
    </>
  )
}

function TerminationOptionSection() {
  const form = useConsumptionPointFormContext()
  const terminationOption = form.watch("contractInfo.terminationOption")

  if (terminationOption === "DO_NOT_TERMINATE") {
    return (
      <>
        <DateInputBlock
          label="Datum výpovědi současné smlouvy"
          form={form}
          dateFieldName="contractInfo.contractEndDate"
          monthFieldName="contractInfo.contractEndMonth"
          yearFieldName="contractInfo.contractEndYear"
        />

        <Spacer size={theme.space.s8} />

        <Controller
          control={form.control}
          name="contractInfo.contractTermination"
          render={({ field: { value, ref, onChange, onBlur } }) => {
            const fileError = getFormError({ form, name: "contractInfo.contractTermination" })
            const typeError = getFormError({ form, name: "contractInfo.contractTermination.type" })
            const sizeError = getFormError({ form, name: "contractInfo.contractTermination.size" })

            const errorMessage = fileError ?? typeError ?? sizeError

            return (
              <FormItem>
                <FormLabel>Ukončení smlouvy se stávajícím dodavatelem</FormLabel>
                <FileInput
                  id="consumptio-point-current-contract-termination"
                  uploadedFileName={errorMessage ? undefined : value?.name}
                  error={errorMessage}
                  ref={ref}
                  onClear={() => form.resetField("contractInfo.contractTermination", { defaultValue: "" })}
                  onChange={(e) => {
                    onChange(e.target.files?.[0])
                    // we are not interacting with the input element itself, but with a custom label,
                    // therefore need to trigger onBlur manually in order to trigger onBlur validation
                    onBlur()
                  }}
                />
              </FormItem>
            )
          }}
        />

        <Spacer size={theme.space.s3} />

        <FileInputDisclaimer />
      </>
    )
  }

  return <TerminateContractOptionSection />
}

function TerminateContractOptionSection() {
  const form = useConsumptionPointFormContext()
  const contractType = form.watch("contractInfo.currentContract.contractType")

  function renderContractTypeSection() {
    if (contractType === "FIXED_TERM") {
      return (
        <>
          <DateInputBlock
            label="Platnost smlouvy do"
            form={form}
            dateFieldName="contractInfo.currentContract.contractEndDate"
            monthFieldName="contractInfo.currentContract.contractEndMonth"
            yearFieldName="contractInfo.currentContract.contractEndYear"
          />
          <Spacer size={theme.space.s9} />

          <Controller
            control={form.control}
            name="contractInfo.currentContract.terminationNoticeInDaysBeforeEnd"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Kolik dnů před ukončením smlouvy je možné nejpozději podat výpověď u vašeho stávajícího dodavatele?
                </FormLabel>
                <FormInput
                  id="terminationNoticeInDaysBeforeEnd"
                  type="number"
                  placeholder="Vyplňte počet dní"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(e) => {
                    const value = e.target.value.length === 0 ? null : round(Number.parseInt(e.target.value), 0)
                    field.onChange(value)
                  }}
                  error={getFormError({ form, name: "contractInfo.currentContract.terminationNoticeInDaysBeforeEnd" })}
                  success={getFormSuccess({
                    form,
                    name: "contractInfo.currentContract.terminationNoticeInDaysBeforeEnd",
                  })}
                />
              </FormItem>
            )}
          />

          <Spacer size={theme.space.s2} />
          <InputDisclaimer label="Tuto informaci byste měli najít ve smlouvě. Pokud informaci nenajdete, nechte políčko prázdné." />
        </>
      )
    }

    if (contractType === "VARIABLE_TERM") {
      return (
        <Controller
          control={form.control}
          defaultValue="ONE_MONTH"
          name="contractInfo.currentContract.noticePeriod"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Výpovědní lhůta</FormLabel>
              <FormSelect
                id="noticePeriod"
                options={noticePeriodOptions}
                {...field}
                error={getFormError({ form, name: "contractInfo.currentContract.noticePeriod" })}
                success={getFormSuccess({ form, name: "contractInfo.currentContract.noticePeriod" })}
              />
            </FormItem>
          )}
        />
      )
    }
  }

  return (
    <>
      <Controller
        control={form.control}
        defaultValue="VARIABLE_TERM"
        name="contractInfo.currentContract.contractType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Platnost smlouvy</FormLabel>
            <FormSelect
              id="contractType"
              options={contractTypeOptions}
              {...field}
              error={getFormError({ form, name: "contractInfo.currentContract.contractType" })}
              success={getFormSuccess({ form, name: "contractInfo.currentContract.contractType" })}
            />
          </FormItem>
        )}
      />

      <Spacer size={theme.space.s9} />

      {renderContractTypeSection()}
    </>
  )
}
